import React from 'react'
import { useRouter } from 'next/router'
import { useTheme } from '@mui/material/styles'
import { Box, Flex, Text } from '@fundamentals'
import { Button } from '@common/material'
import { localizedStrings } from '@core/strings'
import { PageUrls } from '@core/page-urls'
import LinkIcon from '@mui/icons-material/Link'
import { TemplateVersionTitle } from '@elements/TemplateVersionTitle'
import { PermitDetailsHeaderProps } from '@modules/permitDetails/types'
import Chips from '@modules/permitDetails/permitDetailsHeader/Chips'
import SecondaryActions from './SecondaryActions'
import { useProductAccess } from 'webapp/providers/authentication'

const PermitDetailsHeader: React.FC<PermitDetailsHeaderProps> = ({
  permit,
}) => {
  const theme = useTheme()
  const router = useRouter()
  const { isViewMode } = useProductAccess()
  const parentPermit = permit?.parentPermit
  const children = permit?.children ?? []

  return (
    <Box boxShadow={`inset 0 -1px ${theme.palette.grey[10]}`}>
      {parentPermit && (
        <Button
          variant='text'
          size='small'
          startIcon={<LinkIcon />}
          onClick={() =>
            router.push(
              parentPermit.project?.id
                ? PageUrls.projectPermitDetail({
                    organisationId: parentPermit.organisationId,
                    projectId: parentPermit.project.id,
                    permitId: parentPermit.id,
                  })
                : PageUrls.permit(parentPermit.organisationId, parentPermit.id),
            )
          }
          sx={{ mb: 1, fontSize: 13, textTransform: 'none' }}
          data-test='parent-permit-link'
        >
          {localizedStrings.linkedToPermit(
            parentPermit.template?.name,
            parentPermit.shortUUID,
          )}
        </Button>
      )}
      {children.length > 0 && (
        <Flex alignItems='center' sx={{ mb: 1 }}>
          <LinkIcon sx={{ fontSize: 16, color: 'primary.main', mr: 0.5 }} />
          <Text
            fontSize={13}
            color='text.secondary'
            data-test='child-permits-summary'
          >
            {localizedStrings.childPermitsSummary(
              children.length,
              children
                .map((c) => c.template?.name)
                .filter(Boolean)
                .join(', '),
            )}
          </Text>
        </Flex>
      )}
      <Flex mb={1.5} alignItems={'center'}>
        {permit?.template?.colour && (
          <Box
            bgcolor={permit?.template?.colour}
            borderRadius={2}
            width={15}
            height={15}
            mr={1}
          />
        )}
        <TemplateVersionTitle
          dataTest='permit-details-title'
          organisationId={permit.organisationId}
          template={permit.template}
          reference={permit.shortUUID}
          getTemplateUrl={PageUrls.editTemplate}
          getVersionUrl={PageUrls.editTemplateVersion}
        />
      </Flex>

      <Flex pb='1.2rem' flexDirection='row' justifyContent='space-between'>
        <Box>
          <Chips permit={permit} />
        </Box>
        <Box>{!isViewMode && <SecondaryActions permit={permit} />}</Box>
      </Flex>
    </Box>
  )
}

export { PermitDetailsHeader }
